// Экран «Пользователи» (п.16) — пятый экран нового фронта (п.20, 2026-09-07).
// Только для администратора: список пользователей (админ/активен/роли в проектах,
// сброс пароля) и участники текущего проекта с ролями.
// Организации (#32, 2026-09-13): администратор видит только свою организацию,
// владелец сервиса — всех, заводит организации и выбирает организацию новому пользователю.
// Роли — прямо в строке пользователя, столбец на каждый проект; пароль — показать и скопировать
// только что заданный (владелец 2026-09-16): сервер хранит лишь хэш, прежний пароль не узнать.
import { useCallback, useEffect, useState } from 'react';
import { ask, confirmBox } from '../dialog';
import { call, p, type Data } from '../api';
import type { ShellCtx } from '../shell';

type User = Data<'/api/users', 'get'>[number];
type Member = Data<'/api/projects/{pid}/members', 'get'>[number];
type Project = Data<'/api/projects', 'get'>[number];
type Podr = Data<'/api/projects/{pid}/podryadchiki', 'get'>[number];
type Org = Data<'/api/organizations', 'get'>[number];
type Me = Data<'/api/auth/me', 'get'>;

const ROLES = ['планер', 'подрядчик', 'заказчик', 'администратор'];

/** Пароль без похожих символов (0/O, 1/l/I) — его диктуют и переписывают руками. */
function pridumatParol(dlina = 12) {
  const abc = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const buf = new Uint32Array(dlina);
  crypto.getRandomValues(buf);
  return Array.from(buf, x => abc[x % abc.length]).join('');
}

async function skopirovat(tekst: string) {
  try { await navigator.clipboard.writeText(tekst); return true; } catch { return false; }
}

export function Users({ ctx }: { ctx: ShellCtx }) {
  const [users, setUsers] = useState<User[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [podrPo, setPodrPo] = useState<Record<number, string[]>>({});
  // пароли, заданные в этом окне (создание, сброс): только в памяти страницы, не на сервере
  const [paroli, setParoli] = useState<Record<number, string>>({});
  const [vidno, setVidno] = useState<Record<string, boolean>>({});
  // выбрана роль «подрядчик», но подрядчик ещё не указан: ключ «uid:pid»
  const [zhdetPodr, setZhdetPodr] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState('');
  const [nu, setNu] = useState({ email: '', name: '', password: '', is_admin: false, org_id: '' });
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [novaya, setNovaya] = useState('');
  const [mem, setMem] = useState({ user: '', role: 'планер', podryadchik: '' });

  const fail = (m: string) => { setMsg(m); ctx.onError?.(m); };
  const saved = () => { setMsg(''); ctx.onSaved?.(); };

  const load = useCallback(async () => {
    const [u, m, pr, o, mm] = await Promise.all([
      call<User[]>('get', '/api/users'),
      call<Member[]>('get', p('/api/projects/{pid}/members', { pid: ctx.pid })),
      call<Project[]>('get', '/api/projects'),
      call<Org[]>('get', '/api/organizations'),
      call<Me>('get', '/api/auth/me'),
    ]);
    if (!u.ok) { setMsg(u.error.message); return; }
    setUsers(u.data);
    if (o.ok) setOrgs(o.data);
    if (mm.ok) setMe(mm.data);
    if (m.ok) setMembers(m.data);
    if (pr.ok) {
      setProjects(pr.data);
      // подрядчик участника — из справочника «Подрядчики» своего проекта (2026-09-14)
      const spiski = await Promise.all(pr.data.map(x =>
        call<Podr[]>('get', p('/api/projects/{pid}/podryadchiki', { pid: x.id }))));
      const po: Record<number, string[]> = {};
      pr.data.forEach((x, i) => { const s = spiski[i]; po[x.id] = s.ok ? s.data.map(d => d.name) : []; });
      setPodrPo(po);
    }
  }, [ctx.pid]);
  const podr = podrPo[ctx.pid] || [];
  useEffect(() => { void load(); }, [load]);

  const pn = (id: number) => projects.find(x => x.id === id)?.name || '#' + id;

  async function addUser() {
    const r = await call<{ id: number }>('post', '/api/users', { email: nu.email.trim(), name: nu.name.trim(),
      password: nu.password, is_admin: nu.is_admin, org_id: nu.org_id ? +nu.org_id : null });
    if (!r.ok) { fail(r.error.message); return; }
    const pw = nu.password;
    setParoli(s => ({ ...s, [r.data.id]: pw }));
    setNu({ email: '', name: '', password: '', is_admin: false, org_id: '' });
    saved(); void load();
  }
  async function patchUser(u: User, body: Record<string, unknown>) {
    const r = await call('patch', p('/api/users/{uid}', { uid: u.id }), body);
    if (!r.ok) { fail(r.error.message); void load(); return; }
    saved(); void load();
  }
  async function resetPass(u: User) {
    const pw = await ask('Новый пароль для ' + (u.name || u.email) + ' (от 8 символов). Предложен случайный — '
      + 'можно заменить своим. После сохранения его можно посмотреть и скопировать в столбце «Пароль».',
      pridumatParol());
    if (!pw) return;
    const r = await call('patch', p('/api/users/{uid}', { uid: u.id }), { password: pw });
    if (!r.ok) { fail(r.error.message); return; }
    setParoli(s => ({ ...s, [u.id]: pw }));
    setVidno(s => ({ ...s, [u.id]: true }));
    saved();
  }
  async function kopirovat(u: User) {
    const ok = await skopirovat(paroli[u.id]);
    if (ok) { setMsg('Пароль ' + (u.name || u.email) + ' скопирован'); ctx.onSaved?.(); }
    else fail('Браузер не дал скопировать — выделите пароль и скопируйте вручную');
  }
  /** Роль пользователя в проекте из строки таблицы: пусто — убрать из проекта. */
  async function naznachitRol(u: User, pid: number, role: string, podryadchik: string | null = null) {
    const klyuch = u.id + ':' + pid;
    if (!role) {
      setZhdetPodr(s => ({ ...s, [klyuch]: false }));
      if (!(u.members || []).some(m => m.project_id === pid)) return;
      if (!await confirmBox('Убрать «' + (u.name || u.email) + '» из проекта «' + pn(pid) + '»?',
        { ok: 'Убрать', opasno: true })) { void load(); return; }
      const r = await call('delete', p('/api/projects/{pid}/members/{uid}', { pid, uid: u.id }));
      if (!r.ok) { fail(r.error.message); void load(); return; }
      saved(); void load(); return;
    }
    if (role === 'подрядчик' && !podryadchik) {
      setZhdetPodr(s => ({ ...s, [klyuch]: true }));    // запишем, когда выберут подрядчика
      return;
    }
    setZhdetPodr(s => ({ ...s, [klyuch]: false }));
    const r = await call('put', p('/api/projects/{pid}/members/{uid}', { pid, uid: u.id }),
      { role, podryadchik: role === 'подрядчик' ? podryadchik : null });
    if (!r.ok) { fail(r.error.message); void load(); return; }
    saved(); void load();
  }
  async function addMember() {
    const uid = +mem.user;
    if (!uid) { fail('Выберите пользователя'); return; }
    if (mem.role === 'подрядчик' && !mem.podryadchik) { fail('Для роли «подрядчик» выберите подрядчика'); return; }
    const r = await call('put', p('/api/projects/{pid}/members/{uid}', { pid: ctx.pid, uid }),
      { role: mem.role, podryadchik: mem.role === 'подрядчик' ? mem.podryadchik : null });
    if (!r.ok) { fail(r.error.message); return; }
    saved(); void load();
  }
  async function addOrg() {
    const name = novaya.trim();
    if (!name) { fail('Дайте организации название'); return; }
    const r = await call('post', '/api/organizations', { name });
    if (!r.ok) { fail(r.error.message); return; }
    setNovaya(''); saved(); void load();
  }
  async function delMember(uid: number) {
    if (!confirm('Убрать пользователя из проекта?')) return;
    const r = await call('delete', p('/api/projects/{pid}/members/{uid}', { pid: ctx.pid, uid }));
    if (!r.ok) { fail(r.error.message); return; }
    saved(); void load();
  }

  const vladelets = !!me?.is_owner;
  return (
    <div>
      {/* организации (#32): видит владелец сервиса, он же заводит новые */}
      {vladelets && (
        <div className="card" style={{ marginBottom: 12 }}>
          <b>Организации</b>
          <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Каждая компания видит только своих пользователей
            и свои проекты; общая библиотека «Глобально» видна администраторам всех организаций.</div>
          <div className="bar" style={{ margin: '0 0 8px' }}>
            <input placeholder="название организации" style={{ minWidth: 240 }} value={novaya}
              onChange={e => setNovaya(e.target.value)} />
            <button onClick={() => void addOrg()}>Добавить организацию</button>
          </div>
          <div className="tbl" style={{ maxHeight: '30vh' }}><table>
            <thead><tr><th>Организация</th><th>Пользователей</th><th>Проектов</th></tr></thead>
            <tbody>{orgs.map(o => (
              <tr key={o.id}><td>{o.name}</td><td className="num">{o.polzovateley}</td><td className="num">{o.proektov}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
      <div className="card" style={{ marginBottom: 12 }}>
        <b>Пользователи{!vladelets && me?.org ? ' · ' + me.org : ''}</b>
        <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Администратор заводит пользователей своей организации
          и назначает им роли в проектах. Роли: <b>администратор</b> — всё в своей организации; <b>планер</b> — модель,
          справочники, факт; <b>подрядчик</b> — факт только по своим работам (укажите, какой подрядчик);
          <b> заказчик</b> — только просмотр.</div>
        <div className="bar" style={{ margin: '0 0 8px' }}>
          <input placeholder="почта" style={{ minWidth: 220 }} value={nu.email} onChange={e => setNu(s => ({ ...s, email: e.target.value }))} />
          <input placeholder="имя" style={{ minWidth: 160 }} value={nu.name} onChange={e => setNu(s => ({ ...s, name: e.target.value }))} />
          <input type={vidno.novyj ? 'text' : 'password'} placeholder="пароль (от 8 символов)" style={{ minWidth: 180 }}
            value={nu.password} onChange={e => setNu(s => ({ ...s, password: e.target.value }))} />
          <button className="sec" title={vidno.novyj ? 'Скрыть пароль' : 'Показать пароль'}
            onClick={() => setVidno(s => ({ ...s, novyj: !s.novyj }))}>{vidno.novyj ? 'Скрыть' : 'Показать'}</button>
          <button className="sec" title="Случайный пароль из 12 символов без похожих букв и цифр"
            onClick={() => { setNu(s => ({ ...s, password: pridumatParol() })); setVidno(s => ({ ...s, novyj: true })); }}>Придумать</button>
          <label><input type="checkbox" style={{ width: 'auto' }} checked={nu.is_admin}
            onChange={e => setNu(s => ({ ...s, is_admin: e.target.checked }))} /> администратор</label>
          {vladelets && (
            <select value={nu.org_id} title="Организация нового пользователя (владелец сервиса)"
              onChange={e => setNu(s => ({ ...s, org_id: e.target.value }))}>
              <option value="">— моя организация —</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button onClick={() => void addUser()}>Добавить пользователя</button>
          <span className="pw-muted">{msg}</span>
        </div>
        <div className="tbl" style={{ maxHeight: '40vh' }}><table>
          <thead><tr><th>Имя</th><th>Почта</th>{vladelets && <th>Организация</th>}<th>Админ</th><th>Активен</th>
            {projects.map(x => <th key={x.id} title={'Роль в проекте «' + x.name + '»; «—» — не участник'}>{x.name}</th>)}
            <th title="Пароль виден только сразу после создания пользователя или сброса пароля: сервер хранит его зашифрованным, прежний пароль узнать нельзя">Пароль</th><th></th></tr></thead>
          <tbody>{users.map(u => (
            <tr key={u.id} className={u.active ? undefined : 'muted'}>
              <td>{u.name || ''}{u.is_owner ? <span className="pw-muted"> · владелец</span> : null}</td><td>{u.email}</td>
              {vladelets && <td>{u.org || '—'}</td>}
              <td className="num"><input type="checkbox" style={{ width: 'auto' }} checked={!!u.is_admin}
                onChange={e => void patchUser(u, { is_admin: e.target.checked })} /></td>
              <td className="num"><input type="checkbox" style={{ width: 'auto' }} checked={!!u.active}
                onChange={e => void patchUser(u, { active: e.target.checked })} /></td>
              {projects.map(x => {
                const m = (u.members || []).find(mm => mm.project_id === x.id);
                const zhdet = !!zhdetPodr[u.id + ':' + x.id];
                const rol = zhdet ? 'подрядчик' : m?.role || '';
                return (
                  <td key={x.id} style={{ whiteSpace: 'nowrap' }}>
                    <select value={rol} disabled={!u.active} title={u.active ? 'Роль в проекте' : 'Пользователь отключён'} style={{ width: 'auto', minWidth: 0, padding: '3px 4px', fontSize: 13 }}
                      onChange={e => void naznachitRol(u, x.id, e.target.value, m?.podryadchik || null)}>
                      <option value="">—</option>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    {rol === 'подрядчик' && (
                      <select value={zhdet ? '' : m?.podryadchik || ''} style={{ marginLeft: 4, width: 'auto', minWidth: 0, maxWidth: 160, padding: '3px 4px', fontSize: 13 }}
                        title="Подрядчик из справочника проекта: пользователь видит и отмечает только его задачи"
                        onChange={e => { if (e.target.value) void naznachitRol(u, x.id, 'подрядчик', e.target.value); }}>
                        <option value="">— подрядчик —</option>
                        {(podrPo[x.id] || []).map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </td>);
              })}
              <td style={{ whiteSpace: 'nowrap' }}>{paroli[u.id]
                ? <>
                    <code style={{ userSelect: 'all' }}>{vidno[u.id] ? paroli[u.id] : '••••••••'}</code>{' '}
                    <button className="sec" onClick={() => setVidno(s => ({ ...s, [u.id]: !s[u.id] }))}>
                      {vidno[u.id] ? 'Скрыть' : 'Показать'}</button>{' '}
                    <button className="sec" onClick={() => void kopirovat(u)}>Копировать</button>
                  </>
                : <span className="pw-muted" title="Сервер хранит пароль зашифрованным — посмотреть прежний нельзя. Задайте новый кнопкой «Сбросить пароль»">скрыт</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}><button className="sec" style={{ padding: '3px 8px', fontSize: 13 }} onClick={() => void resetPass(u)}>Сбросить пароль</button></td>
            </tr>))}</tbody>
        </table></div>
      </div>
      <div className="card">
        <b>Участники проекта «{projects.find(x => x.id === ctx.pid)?.name || ''}»</b>
        <div className="bar" style={{ margin: '8px 0' }}>
          <select value={mem.user} onChange={e => setMem(s => ({ ...s, user: e.target.value }))}>
            <option value="">— пользователь —</option>
            {users.filter(u => u.active).map(u => <option key={u.id} value={u.id}>{(u.name || u.email) + ' <' + u.email + '>'}</option>)}
          </select>
          <select value={mem.role} onChange={e => setMem(s => ({ ...s, role: e.target.value }))}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}</select>
          <select value={mem.podryadchik} title="Подрядчик из справочника «Подрядчики» — для роли «подрядчик»: пользователь видит и отмечает только его задачи"
            onChange={e => setMem(s => ({ ...s, podryadchik: e.target.value }))}>
            <option value="">— подрядчик (для роли подрядчик) —</option>
            {podr.map(x => <option key={x} value={x}>{x}</option>)}</select>
          <button onClick={() => void addMember()}>Добавить в проект</button>
        </div>
        <div className="tbl" style={{ maxHeight: '40vh' }}><table>
          <thead><tr><th>Имя</th><th>Почта</th><th>Роль</th><th>Подрядчик</th><th></th></tr></thead>
          <tbody>{members.map(m => (
            <tr key={m.user_id}>
              <td>{m.name || ''}</td><td>{m.email}</td><td>{m.role}</td><td>{m.podryadchik || ''}</td>
              <td><button className="del" title="убрать из проекта" onClick={() => void delMember(m.user_id)}>×</button></td>
            </tr>))}</tbody>
        </table></div>
      </div>
    </div>
  );
}
